import type { ReactNode } from 'react';
import { useFormat } from '@paige/format';
import { FlowEngineLane } from './FlowEngineLane';
import { FlowGlyph, FlowSpark } from './FlowGlyph';
import { FlowPeek, type PeekState } from './FlowPeek';
import { FlowHalt, FlowStage } from './FlowStage';
import { FlowTrack } from './FlowTrack';
import { pastCell, placeAt, type FlowMinute } from './flow-history';
import type { FlowBursts } from './flow-motion';
import { movedJustNow, type FlowStatus } from './flow-state';
import type { AdminFlow, FlowCell, FlowPart, FlowPlace } from './flow-types';

export interface FlowMapProps {
  flow: AdminFlow;
  bursts: FlowBursts;
  status: (place: FlowPlace) => FlowStatus;
  open: FlowPlace | null;
  toggle: (place: FlowPlace) => void;
  /** A recorded minute shown instead of now; nothing moves in it. */
  past: FlowMinute | null;
  /** The pools and services not up, by the places they feed. */
  starved: Map<FlowPlace, FlowPart>;
  /** Places the cards strip points at. */
  lit: Set<string>;
  peek: PeekState;
  onPeek: (place: FlowPlace, on: boolean) => void;
}

const SPINE: FlowPlace[] = [
  'arriving_api',
  'preparing',
  'reading',
  'splitting',
  'understanding',
  'checking',
];

/** Where both roads end: pages that left export in the last quarter hour. */
function Done({ cell }: { cell: FlowCell }): ReactNode {
  const f = useFormat();
  return (
    <div className={cell.pages15m > 0 ? 'fl-done fl-done--on' : 'fl-done'} data-place="done">
      <span className="fl-stage__head">
        <FlowGlyph tone="done" />
        <span className="fl-stage__name">Done</span>
      </span>
      <strong key={cell.pages15m} className="fl-num">
        {f.int(cell.pages15m)}
      </strong>
      <span className="fl-stage__unit">pages out, last 15 min</span>
      <FlowSpark
        values={cell.series}
        label={`Done: ${f.int(cell.pages15m)} pages in the last 15 minutes`}
      />
    </div>
  );
}

/** A stage and a track of the map, by place, from the map's props; a recorded minute's counts when in the past. */
function mapParts(props: FlowMapProps) {
  const { flow, bursts, status, open, toggle, past, starved, lit, peek, onPeek } = props;
  const now = (place: FlowPlace) => flow.cells.find((c) => c.place === place)!;
  const cell = (place: FlowPlace) =>
    past ? pastCell(now(place), placeAt(past, place)) : now(place);
  const stage = (place: FlowPlace, className: string) => (
    <FlowStage
      key={place}
      cell={cell(place)}
      status={status(place)}
      selected={open === place}
      onSelect={() => toggle(place)}
      className={className}
      starved={past ? null : starved.get(place)}
      lit={lit.has(place)}
      onPeek={(on) => onPeek(place, on)}
      peek={peek.place === place ? <FlowPeek peek={peek} here={status(place).here} /> : null}
    />
  );
  const track = (from: FlowPlace, className: string) => (
    <FlowTrack
      key={`t-${from}`}
      burst={past ? undefined : bursts[from]}
      live={!past && movedJustNow(now(from))}
      className={className}
    />
  );
  return { cell, stage, track };
}

/** The engine road: what arrives from scanners, email, uploads, folders and phones, then the engine's one lane. */
function EngineRoad(props: FlowMapProps): ReactNode {
  const { cell, stage, track } = mapParts(props);
  const tone = props.status('engine').tone;
  return (
    <>
      <span className="fl-lane fl-lane--engine">Scan, email, upload, folders, mobile</span>
      {stage('arriving_engine', 'fl-at-e0')}
      {track('arriving_engine', 'fl-at-te0')}
      <FlowEngineLane
        cell={cell('engine')}
        runs={props.past ? [] : props.flow.runs}
        status={props.status('engine')}
        selected={props.open === 'engine'}
        onSelect={() => props.toggle('engine')}
        halt={<FlowHalt tone={tone} />}
        lit={props.lit.has('engine')}
      />
      {track('engine', 'fl-at-te5')}
    </>
  );
}

/** The two roads as one picture: the spine stage by stage above, the engine road below, joining at review. */
export function FlowMap(props: FlowMapProps): ReactNode {
  const { cell, stage, track } = mapParts(props);
  return (
    <div
      className={props.past ? 'fl-map fl-map--past' : 'fl-map'}
      role="group"
      aria-label="Documents moving through Paige"
    >
      <span className="fl-lane fl-lane--api">API batches</span>
      {SPINE.flatMap((place, i) => [stage(place, `fl-at-a${i}`), track(place, `fl-at-ta${i}`)])}
      <EngineRoad {...props} />
      <span className="fl-lane fl-lane--shared">Both roads</span>
      {stage('review', 'fl-at-review')}
      {track('review', 'fl-at-treview')}
      {stage('export', 'fl-at-export')}
      {track('export', 'fl-at-texport')}
      <Done cell={cell('export')} />
    </div>
  );
}
